import { LoggerlessTransport, LoggerlessTransportConfig, LogLayerTransportParams } from "@loglayer/transport";

export interface RecentLogEntry {
    /** ISO 8601 */
    time: string;
    level: string;
    message: string;
    attributes?: Record<string, string | number | boolean | null>;
}

export interface RecentLogsTransportConfig extends LoggerlessTransportConfig {
    /** Number of logs kept, the oldest are dropped first. Default: 500 */
    capacity?: number;
}

/**
 * LogLayer transport keeping the last logs in memory, so that a support diagnostic can include what happened before the
 * user reported a problem (`recentLogs.entries()`).
 *
 * The attributes are kept as primitives (the other values are converted to strings) so that the transport neither
 * retains the logged objects nor fails to serialize them.
 */
export class RecentLogsTransport extends LoggerlessTransport {
    private readonly capacity: number;
    private readonly buffer: RecentLogEntry[] = [];

    public constructor({ capacity = 500, ...config }: RecentLogsTransportConfig = {}) {
        super(config);
        this.capacity = capacity;
    }

    public shipToLogger({ logLevel, messages, data, hasData }: LogLayerTransportParams): unknown[] {
        const entry: RecentLogEntry = {
            time: new Date().toISOString(),
            level: String(logLevel),
            message: messages.map((message) => toText(message)).join(" "),
        };
        if (hasData && data) {
            entry.attributes = Object.fromEntries(
                Object.entries(data)
                    .filter(([, value]) => value !== undefined)
                    .map(([key, value]) => [key, toPrimitive(value)]),
            );
        }

        this.buffer.push(entry);
        if (this.buffer.length > this.capacity) {
            this.buffer.splice(0, this.buffer.length - this.capacity);
        }
        return messages;
    }

    /** The kept logs, oldest first */
    public entries(): RecentLogEntry[] {
        return [...this.buffer];
    }
}

function toPrimitive(value: unknown): string | number | boolean | null {
    switch (typeof value) {
        case "string":
        case "number":
        case "boolean":
            return value;
        default:
            return value === null ? null : toText(value);
    }
}

function toText(value: unknown): string {
    if (typeof value === "string") {
        return value;
    }
    if (value instanceof Error) {
        return `${value.name}: ${value.message}`;
    }
    try {
        return JSON.stringify(value) ?? String(value);
    } catch {
        return String(value);
    }
}
